import { FC, useState } from 'react';
import { ActionIcon, Group, Loader, Text } from '@mantine/core';
import { IconDownload, IconPaperclip } from '@tabler/icons';
import { ImapAttachmentDTO } from '../../Types/MailT';
import { FileS } from '../../Service/FileS';
import { openFileViewModal } from '../../Molecules/Dialogs/FileViewModal';
import { MailS } from '../../Service/MailS';
import { ToastS } from '../../Service/ToastS';

type Props = {
  file: ImapAttachmentDTO;
  messageId: number | string;
  configId?: number;
  folderName?: string;
};

const ImapAttachment: FC<Props> = ({ file, messageId, configId, folderName }) => {
  const { id, fileName, contentType, size } = file;
  const [loading, setLoading] = useState(false);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);

  const loadFile = async () => {
    if (fileBlob) {
      return fileBlob;
    }
    if (configId === undefined || !folderName) {
      ToastS.error('imap-attachment-missing-context', 'Datei konnte nicht geladen werden.');
      return null;
    }

    setLoading(true);
    try {
      const res = await MailS.getMessageAttachmentIMAP(messageId, configId, folderName, id);
      if (res) {
        const blob = await res.blob();
        setFileBlob(blob);
        return blob;
      }
      ToastS.generalError();
    } catch (e) {
      console.error(e);
      ToastS.generalError();
    } finally {
      setLoading(false);
    }
    return null;
  };

  const download = async () => {
    if (loading) return;
    const blob = await loadFile();
    if (!blob) return;
    FileS.download(fileName, blob);
  };

  const showPreview = async () => {
    if (loading) return;
    const file = await loadFile();
    if (!file) return;
    openFileViewModal({ fileName, file, contentType });
  };

  return (
    <Group
      my="md"
      position="apart"
      p="xs"
      sx={(theme) => ({
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
        maxWidth: 'fit-content',
      })}
    >
      <Text
        weight="bold"
        lineClamp={1}
        sx={(theme) => ({
          cursor: loading ? 'wait' : 'pointer',
          color: theme.colorScheme === 'dark' ? theme.colors.blue[1] : theme.colors.blue[4],
        })}
        variant="link"
        onClick={loading ? undefined : showPreview}
      >
        {loading ? (
          <Loader size={14} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />
        ) : (
          <IconPaperclip size={16} style={{ marginRight: 5 }} />
        )}
        {fileName}
        {size !== undefined && (
          <Text span color="dimmed" weight="normal" size="xs">
            {' '}
            ({FileS.getFileSize(size)})
          </Text>
        )}
      </Text>
      <ActionIcon size="xs" onClick={loading ? undefined : download} loading={loading} disabled={loading}>
        <IconDownload size={16} />
      </ActionIcon>
    </Group>
  );
};

export default ImapAttachment;
